import type { AreaOfLaw, CaseTaxonomyContextInput } from './caseTaxonomyRegistry';
import {
  inferCategoryFromMatterType,
  resolveMatterTypeId,
} from './caseTaxonomyRegistry';
import {
  getCaseStageOptions,
  resolveStartingCaseStage,
  stageLabelsMatch,
} from './caseStages';
import {
  CRIMINAL_CASE_TAXONOMY_CONFIG,
  findCriminalProceedingById,
  resolveLegacyCriminalProceeding,
} from './criminalTaxonomy';

export type MatterTypePickerMode = 'standard' | 'custom';

export interface MatterTypePickerValue {
  mode: MatterTypePickerMode;
  matterTypeId: string;
  customAreaLabel?: string;
  customMatterType?: string;
  customDescription?: string;
  closestStandardArea?: AreaOfLaw | '';
}

export interface MatterTypeCaseContext {
  stage?: string;
  caseCategory?: string;
  matterType?: string;
  caseType?: string;
  representingSide?: string;
  isCustomCase?: boolean;
  criminalCategory?: string;
  proceedingType?: string;
  closestStandardArea?: string;
  caseSubtype?: string;
}

export interface MatterTypeUpdateResult {
  update: Record<string, unknown>;
  stageWillChange: boolean;
  newStage?: string;
  previousStage?: string;
}

export interface CustomMatterTypeInput {
  areaLabel: string;
  matterType: string;
  description?: string;
  closestStandardArea?: AreaOfLaw | '';
}

function resolveStageAfterMatterTypeChange(
  caseData: MatterTypeCaseContext,
  newCaseData: CaseTaxonomyContextInput,
  options?: { alwaysReset?: boolean },
): { stage: string; stageWillChange: boolean; previousStage?: string } {
  const currentStage = caseData.stage?.trim() || '';
  const representingSide = caseData.representingSide;

  if (!options?.alwaysReset && currentStage) {
    const newStageOptions = getCaseStageOptions(newCaseData);
    const inList = newStageOptions.find((s) => stageLabelsMatch(s, currentStage));
    if (inList) {
      return { stage: inList, stageWillChange: false, previousStage: currentStage };
    }
  }

  const startingStage = resolveStartingCaseStage(newCaseData, representingSide);
  return {
    stage: startingStage,
    stageWillChange: !currentStage || !stageLabelsMatch(currentStage, startingStage),
    previousStage: currentStage || undefined,
  };
}

function buildCriminalFields(matterTypeId: string): Record<string, string> {
  const resolved = resolveLegacyCriminalProceeding(matterTypeId);
  if (resolved) {
    return {
      criminalCategory: resolved.categoryId,
      proceedingType: resolved.proceedingId,
    };
  }
  const proceeding = findCriminalProceedingById(CRIMINAL_CASE_TAXONOMY_CONFIG, matterTypeId);
  if (proceeding?.criminalCategoryId) {
    return {
      criminalCategory: proceeding.criminalCategoryId,
      proceedingType: proceeding.id,
    };
  }
  return {};
}

export function matterTypePickerValueFromCase(
  caseData: MatterTypeCaseContext,
): MatterTypePickerValue {
  if (caseData.isCustomCase) {
    return {
      mode: 'custom',
      matterTypeId: '',
      customAreaLabel: caseData.caseCategory || '',
      customMatterType: caseData.matterType || caseData.caseType || '',
      customDescription: caseData.caseSubtype || '',
      closestStandardArea: (caseData.closestStandardArea as AreaOfLaw | '') || '',
    };
  }

  const matterTypeId =
    resolveMatterTypeId(caseData.matterType || caseData.caseType) ||
    caseData.matterType ||
    caseData.caseType ||
    'Civil Suit';

  return {
    mode: 'standard',
    matterTypeId,
  };
}

/** Build picker value from DOCX import review data. */
export function matterTypePickerValueFromExtracted(caseData: {
  isCustomCase?: boolean;
  caseCategory?: string;
  matterType?: string;
  caseType?: string;
  caseSubtype?: string;
  closestStandardArea?: string;
}): MatterTypePickerValue {
  return matterTypePickerValueFromCase(caseData);
}

export function buildStandardMatterTypeUpdate(
  matterTypeId: string,
  caseData: MatterTypeCaseContext,
  options?: { alwaysResetStage?: boolean },
): MatterTypeUpdateResult {
  const resolvedId = resolveMatterTypeId(matterTypeId) || matterTypeId;
  const caseCategory = inferCategoryFromMatterType(resolvedId) ?? 'Civil';

  const newContext: CaseTaxonomyContextInput = {
    caseCategory,
    matterType: resolvedId,
    caseType: resolvedId,
  };

  const { stage, stageWillChange, previousStage } = resolveStageAfterMatterTypeChange(
    caseData,
    newContext,
    { alwaysReset: options?.alwaysResetStage },
  );

  const update: Record<string, unknown> = {
    matterType: resolvedId,
    caseType: resolvedId,
    caseCategory,
    caseMatter: caseCategory,
    isCustomCase: false,
    closestStandardArea: '',
    caseSubtype: '',
    stage,
    criminalCategory: '',
    proceedingType: '',
  };

  if (caseCategory === 'Criminal') {
    Object.assign(update, buildCriminalFields(resolvedId));
  }

  return {
    update,
    stageWillChange,
    newStage: stageWillChange ? stage : undefined,
    previousStage,
  };
}

export function buildCustomMatterTypeUpdate(
  input: CustomMatterTypeInput,
  caseData: MatterTypeCaseContext,
  options?: { alwaysResetStage?: boolean },
): MatterTypeUpdateResult {
  const areaLabel = input.areaLabel.trim();
  const matterType = input.matterType.trim();
  const description = input.description?.trim() || '';
  const closestStandardArea = input.closestStandardArea || '';

  const newContext: CaseTaxonomyContextInput = {
    caseCategory: areaLabel,
    matterType,
    caseType: matterType,
    isCustomCase: true,
    closestStandardArea,
  };

  const { stage, stageWillChange, previousStage } = resolveStageAfterMatterTypeChange(
    caseData,
    newContext,
    { alwaysReset: options?.alwaysResetStage },
  );

  const update: Record<string, unknown> = {
    isCustomCase: true,
    caseCategory: areaLabel,
    matterType,
    caseType: matterType,
    caseMatter: areaLabel,
    caseSubtype: description,
    closestStandardArea,
    stage,
    criminalCategory: '',
    proceedingType: '',
  };

  return {
    update,
    stageWillChange,
    newStage: stageWillChange ? stage : undefined,
    previousStage,
  };
}

export function buildMatterTypeUpdateFromPicker(
  value: MatterTypePickerValue,
  caseData: MatterTypeCaseContext,
  options?: { alwaysResetStage?: boolean },
): MatterTypeUpdateResult {
  if (value.mode === 'custom') {
    return buildCustomMatterTypeUpdate(
      {
        areaLabel: value.customAreaLabel || '',
        matterType: value.customMatterType || '',
        description: value.customDescription,
        closestStandardArea: value.closestStandardArea,
      },
      caseData,
      options,
    );
  }

  return buildStandardMatterTypeUpdate(value.matterTypeId, caseData, options);
}

export function isMatterTypePickerValueValid(value: MatterTypePickerValue): boolean {
  if (value.mode === 'custom') {
    return Boolean(value.customAreaLabel?.trim() && value.customMatterType?.trim());
  }
  return Boolean(value.matterTypeId?.trim());
}
